import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { mapUserLanguageToCode } from '../Utils/languageUtils';

// define the initial state
const initialState = {
  user: null, // Initially no user is logged in
  role: null, // Initially no user role is set
  branch_name: null,
  token: null,
  appLanguage: 'en', // Top-level primitive for instant translation reactivity
};

const useUserStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      // Function to set user on login
      setUser: (userData) => set({
        user: userData,
        appLanguage: mapUserLanguageToCode(userData?.language || 'en'),
      }),

      // Function to update only the language field instantly (for Edit Profile)
      setUserLanguage: (lang) =>
        set((state) => ({
          user: state.user ? { ...state.user, language: lang } : state.user,
          appLanguage: lang,
        })),

      // Function to set user role on login
      setRole: (role) => set({ role: role }),

      // Function to set user token on login
      setToken: (token) => set({ token: token }),

      setIsSubscribed: (isSubscribed) => {
        const { user } = get();
        set({ user: { ...user, is_subscribed: isSubscribed } });
      },

      // Function to set user selected branch on login
      setSelectedBranch: (selectedBranch) => {
        const { user } = get();
        set({ user: { ...user, selected_branch: selectedBranch } });
      },

      setBranchName: (branchName) => set({ branch_name: branchName }),

      // Function to set user profile completion status on login
      setIsProfileCompleted: (isProfileCompleted) => {
        const { user } = get();
        set({ user: { ...user, complete_profile: isProfileCompleted } });
      },

      // Function to set user new account type on login
      setNewAccountType: (type) => {
        const { user } = get(); // Get the current user from the state
        set({ user: { ...user, party_ledgers_account_type: type } });
      },

      // Function to clear user on logout
      clearUser: () => set(initialState),

      // ✅ Add helper functions for access
      getMembershipLevel: () => {
        const { user } = get();
        if (!user) return 'Guest';

        // Check subscription_type field (case-insensitive)
        const subscriptionType = (user.subscription_type || '').toLowerCase().trim();
        if (subscriptionType === 'premium') {
          return 'Premium';
        }
        if (subscriptionType === 'standard') {
          return 'Standard';
        }

        // Check for active subscription
        if (user.is_subscribed) {
          const planName = (user.subscription?.product_name || '').toLowerCase().trim();

          // Check if it's Premium (case-insensitive) - more precise check
          if (planName.includes('premium')) {
            return 'Premium';
          }

          // Check if it's Standard (case-insensitive) - more precise check
          if (planName.includes('standard')) {
            return 'Standard';
          }

          // If subscribed but plan name doesn't match, default to Standard
          // This handles cases where subscription exists but plan name might be missing or different format
          return 'Standard';
        }

        // Check for 30-day trial
        if (user.created_at) {
          const createdAt = new Date(user.created_at);
          const now = new Date();
          const diffTime = Math.abs(now - createdAt);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays <= 30) {
            return 'Trial';
          }
        }

        return 'Basic';
      },

      hasFullAccess: () => {
        const { getMembershipLevel } = get();
        const level = getMembershipLevel();
        return ['Standard', 'Premium', 'Trial'].includes(level);
      },

      canReadMessages: () => {
        const { getMembershipLevel } = get();
        const level = getMembershipLevel();
        return ['Standard', 'Premium', 'Trial'].includes(level);
      },

      canPlayGames: () => {
        const { getMembershipLevel } = get();
        const level = getMembershipLevel();
        return ['Standard', 'Premium', 'Trial'].includes(level);
      },

      canVirtualDate: () => {
        const { getMembershipLevel } = get();
        const level = getMembershipLevel();
        return ['Standard', 'Premium', 'Trial'].includes(level);
      },

      canSeeViews: () => {
        const { getMembershipLevel } = get();
        const level = getMembershipLevel();
        return ['Standard', 'Premium', 'Trial'].includes(level);
      },

      canFavorite: () => {
        const { getMembershipLevel } = get();
        const level = getMembershipLevel();
        return ['Standard', 'Premium', 'Trial'].includes(level);
      },

      // Premium Features
      isPremium: () => {
        const { getMembershipLevel } = get();
        const level = getMembershipLevel();
        return ['Premium', 'Trial'].includes(level);
      },

      canGoIncognito: () => {
        const { isPremium } = get();
        return isPremium();
      },

      canAutoReply: () => {
        const { isPremium } = get();
        return isPremium();
      },

      hasPremiumBadge: () => {
        const { isPremium } = get();
        return isPremium();
      },

      canControlVisibility: () => {
        const { isPremium } = get();
        return isPremium();
      },

      canControlMessageReception: () => {
        const { isPremium } = get();
        return isPremium();
      },
    }),
    {
      name: 'user-storage', // Key for localStorage
      partialize: (state) => ({ user: state.user, role: state.role, appLanguage: state.appLanguage }),
      // Handle migration from old localStorage that doesn't have appLanguage
      onRehydrateStorage: () => (state) => {
        if (state && state.user && !state.appLanguage) {
          state.appLanguage = mapUserLanguageToCode(state.user.language || 'en');
        }
      },
    }
  )
);

export default useUserStore;
